import * as yup from 'yup';
import { Feature, FeatureCollection } from 'geojson';
import { notifyMonitoring } from './monitoring';
import { WrappedNameDiv } from '../components/left-menu/menu-components';
import ArrowIcon from '../components/icons/ArrowIcon';
import {
  RawSection,
  RawVariableList,
  ReportDescription,
  Section,
  VariableList,
  VarListEntry,
} from '../types/soils';

// XML returned by the Web Soil Survey is deeply nested. If a child list has
// only one element, the child object is returned without the list. This helper
// transforms the value so that we can always expect a list.
export const transformArray = <T,>(
  maybeArray: T | T[] | T[][] | undefined,
): T[] => {
  if (maybeArray == null) return [];
  if (Array.isArray(maybeArray)) {
    return maybeArray.filter((item) => item !== null).flatMap(transformArray);
  }
  return [maybeArray];
};

export const listItemSchema = yup.object().shape({
  para: yup.string().nullable(),
});

export const varListEntrySchema = yup.object().shape({
  term: yup.string().nullable(),
  listitem: listItemSchema.required(),
});

export const variableListSchema = yup
  .array()
  .transform(transformArray)
  .of(varListEntrySchema);

export const simpleSectionSchema = yup.object().shape({
  title: yup.string().nullable(),
  variablelist: variableListSchema,
});

const tableTdSchema = yup.lazy((value) =>
  typeof value === 'string'
    ? yup.string()
    : ((yup
        .object()
        .shape({ para: yup.string() }) as unknown) as yup.AnySchema),
);

const tableRowSchema = yup.object().shape({
  td: yup.array().of(tableTdSchema),
});

export const soilReportSchema = yup.object().shape({
  section: yup.array().of(
    yup.object().shape({
      title: yup.string(),
      table: yup.array().of(
        yup.object().shape({
          title: yup.string(),
          col: yup.array().of(yup.string()),
          thead: yup.object().shape({
            tr: tableRowSchema,
          }),
          tbody: yup.object().shape({
            tr: yup.array().of(tableRowSchema),
          }),
        }),
      ),
    }),
  ),
});

// Transform an array to an object with indices as keys. Helper for validating
// structure of data returned by WSS.
export const transformArrayToObject = <T,>(array: T[]) =>
  array.reduce(
    (acc, t: T, i: number) => ({
      ...acc,
      [i]: t,
    }),
    {},
  );

export const transformRawVariableList = (
  rawVariableList: RawVariableList,
): VariableList => ({
  varlistentry: transformArray(rawVariableList.varlistentry),
});

// Transform all arrays in the raw section from the WSS so that the types are
// easier to work with
export const transformRawSection = (rawSection: RawSection): Section => ({
  section: transformArray(rawSection.section).map(transformRawSection),
  table: transformArray(rawSection.table),
  title: rawSection.title,
  variablelist: transformArray(rawSection.variablelist).map(
    transformRawVariableList,
  ),
});

export const handleInvalidData = (name: string, data: unknown, err: unknown) =>
  notifyMonitoring(`${name} data invalid: ${JSON.stringify(data)} (${err})`);

export const validateReport = (
  rawReport: Nullable<RawSection>,
  title: string,
) => {
  if (rawReport == null) return null;
  try {
    const report = transformRawSection(rawReport);
    soilReportSchema.validateSync(report);
    return report;
  } catch (err) {
    handleInvalidData(title, rawReport, err);
    return null;
  }
};

export const filterReportItems = (
  reportItems: {
    name: string;
    colSpan?: { [key: string]: number };
    style?: object;
  }[],
  data: FeatureCollection,
) => {
  const filteredItems = [];
  let table = null;
  let validMapUnit = true;
  for (let i = 0; i < reportItems.length; i += 1) {
    const item = reportItems[i];
    if (item.colSpan?.name != null) {
      if (item.style != null) {
        table = item;
        validMapUnit = false;
      } else if (
        // Check if the filtered data contains this map unit
        data.features.some((f: Feature) =>
          item.name.includes(f.properties?.mapUnitName),
        )
      ) {
        // If we are in a new table, add the table item
        if (table != null) {
          filteredItems.push(table);
          table = null;
        }
        // Add the valid map unit
        filteredItems.push(item);
        validMapUnit = true;
      } else {
        validMapUnit = false;
      }
    } else if (validMapUnit) {
      filteredItems.push(item);
    }
  }
  return filteredItems;
};

export const getNameToMapUnitKeyMap = (data: FeatureCollection) =>
  Object.fromEntries(
    data.features.flatMap((f) =>
      f.properties ? [[f.properties.name, f.properties.mapUnitKey]] : [],
    ),
  );

export const getMapUnitKeyFromName = (
  name: string,
  nameToKeyMap: { [mapUnitName: string]: string },
) => {
  const nameComponents = name.split('--');
  const mapUnitName = nameComponents.length === 2 ? nameComponents[1] : name;
  return nameToKeyMap[mapUnitName];
};

export const WrappedColumn = {
  maxWidth: '250px',
  width: '100%',
  overflow: 'hidden',
  wordWrap: 'break-word',
  whiteSpace: 'normal',
};

export const formatSoilName = (name: string) => {
  if (name == null) return '';
  const splitName = name.split('--');
  if (splitName.length === 2) {
    return `${splitName[1]} (${splitName[0]})`;
  }
  return name;
};

export const formatVariableList = (variableList: RawVariableList[]) => {
  if (!variableList) return 'N/A';
  const entries = variableList.flatMap((vl: RawVariableList) =>
    transformArray(vl.varlistentry),
  );
  return (
    <div>
      {entries.map((vle: VarListEntry, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <WrappedNameDiv key={i}>
          <ArrowIcon direction="right" className="mr-1" />
          <span>{[vle.term, vle.listitem.para].join(' ')}</span>
        </WrappedNameDiv>
      ))}
    </div>
  );
};

export const csvFormatVariableList = (variableList: RawVariableList[]) => {
  if (!variableList) return null;
  const entries = variableList.flatMap((vl: RawVariableList) =>
    transformArray(vl.varlistentry),
  );
  return entries
    .map((vle: VarListEntry) => `${vle.term} ${vle.listitem.para}`)
    .join('; ');
};

export const formatReportDescription = (
  reportDescription: Nullable<ReportDescription[]>,
) => {
  if (!reportDescription) return null;
  return (
    <div>
      {(reportDescription[0].section || []).flatMap((descSection, i) => {
        const { para, title } = descSection;
        if (para != null) {
          return (
            // eslint-disable-next-line react/no-array-index-key
            <p key={`para-${i}`}>
              {para.flatMap((p, j) =>
                '#text' in p
                  ? [
                      // eslint-disable-next-line react/no-array-index-key
                      <span key={j}>
                        {j === 0 ||
                        p['#text'].startsWith('.') ||
                        p['#text'].startsWith(',')
                          ? ''
                          : ' '}
                        {p['#text']}{' '}
                      </span>,
                    ]
                  : 'emphasis' in p
                  ? [
                      <span
                        className="font-italic"
                        key={j} // eslint-disable-line react/no-array-index-key
                      >
                        {p.emphasis[0]['#text']}
                      </span>,
                    ]
                  : 'citation' in p
                  ? [
                      <span
                        key={j} // eslint-disable-line react/no-array-index-key
                      >
                        {p.citation[0]['#text']}
                      </span>,
                    ]
                  : [],
              )}
            </p>
          );
        }
        if (title != null) {
          // eslint-disable-next-line react/no-array-index-key
          return <p key={`title-${i}`}>{title.map((t) => `${t['#text']}:`)}</p>;
        }
        return [];
      })}
    </div>
  );
};

export const formatSoilFeatureName = (feature: Feature) =>
  `${feature.properties?.name} (${feature.properties?.mapUnitSymbol})`;
